import axios from 'axios'
import config from '@/config'
import { ENV_DEV_OR_STAGING } from '@/helpers/environment'
import Sentry from '@/modules/Sentry'
import type { AppArea } from '@/funnels/@types'

/**
 * One entry of the hand-maintained FwF registry (JSON array on S3, `FwfResolverUrl`).
 *
 * - `url`            slug matched under `/fwf/` (leading slash included, e.g. `/water-fasting`)
 * - `config`         S3 URL to the funnel config — a folder prefix (loader appends
 *                    `/<locale>.json`) or an exact `.json` file
 * - `isActive`       when false, `/fwf/<url>` redirects to `relatedAppArea` instead of rendering
 * - `preview`        when true the pre-funnel is still being TRIED: dev/staging render it,
 *                    production behaves as if it were not in the registry at all (the
 *                    visitor gets the `relatedAppArea` landing). Promoting a funnel to
 *                    production is the builder clearing this flag — one registry, one
 *                    entry per funnel, no second file to keep in sync.
 * - `relatedAppArea` where the pre-funnel hands off: the destination's funnel on completion,
 *                    its landing on disabled/error. One appArea, or a LIST of conditional
 *                    destinations — see {@link FwfTarget}.
 * - `page`           optional: target-funnel screen slug to RESUME at after the pre-funnel,
 *                    skipping the target's first screens the pre-funnel replaced (e.g.
 *                    `focus-area`). Omit to start the target funnel from its first screen.
 *                    A destination's own `page` overrides it.
 * - `hiddenParentScreens` optional: urlSlugs of the parent-funnel entry screens this
 *                    pre-funnel cloned+re-contented (smart crossbreeding). They are removed
 *                    from the parent config when it renders after the handoff, so the user
 *                    never sees the same entry screens twice. Omit/[] to hide nothing.
 */
export interface FwfEntry {
	url: string
	config: string
	isActive: boolean
	relatedAppArea: AppArea | FwfTarget[]
	preview?: boolean
	page?: string
	hiddenParentScreens?: string[]
}

/**
 * One destination of a pre-funnel, and the condition that picks it.
 *
 * This is the registry's JSON-safe mirror of a funnel config's `redirectTo`
 * (`src/funnels/@types`): the same condition keys with the same precedence —
 * `always` short-circuits, otherwise the entry whose `sex` matches — plus
 * `RedirectToConfig`'s `page`. `resolveFwfTarget` evaluates them exactly as
 * `SurveyManager.redirectToNextSurvey` evaluates `redirectTo`, so a pre-funnel
 * that forks behaves like a main gender-selection funnel that forks.
 *
 * A bare `relatedAppArea: 'activity-female-walking'` is the single-destination
 * form and means the same as `[{ appArea: 'activity-female-walking', always: true }]`
 * — every pre-funnel published before the list form reads that way, unchanged.
 */
export interface FwfTarget {
	appArea: AppArea
	/** Unconditional destination — `redirectTo.always`. Wins over any `sex`. */
	always?: true
	/** The answered gender this destination serves — `redirectTo.sex[male|female]`. */
	sex?: 'male' | 'female'
	/** Target-funnel screen slug to resume at — `RedirectToConfig.page`. */
	page?: string
}

/**
 * The entry's destinations, in registry order, whichever form it was authored in —
 * each one normalized, so a consumer reads the same shape either way. The entry's
 * own `page` is the default for destinations that don't name one.
 */
function fwfTargets(entry: FwfEntry | null | undefined): FwfTarget[] {
	const related = entry?.relatedAppArea
	const withPage = (target: FwfTarget): FwfTarget => {
		const page = target.page ?? entry?.page
		return page ? { ...target, page } : target
	}
	if (typeof related === 'string') return related ? [withPage({ appArea: related, always: true })] : []
	if (!Array.isArray(related)) return []
	return related
		.filter((t): t is FwfTarget => Boolean(t) && typeof t.appArea === 'string' && t.appArea.length > 0)
		.map(withPage)
}

/**
 * The destination for a given answered sex, by `redirectToNextSurvey`'s rule:
 * an `always` destination short-circuits, otherwise the one whose `sex` matches.
 *
 * With no recognisable answer — the fork not reached yet (bootstrap), or answered
 * `prefer_not_to_say` — the FIRST destination stands. The publisher writes the
 * female fork first, so that is the same fallback `relatedAppArea` was before the
 * list form, and the same one the disabled/error landing redirect uses.
 */
export function resolveFwfTarget(entry: FwfEntry | null | undefined, userSex?: string | null): FwfTarget | null {
	const targets = fwfTargets(entry)
	if (!targets.length) return null
	const always = targets.find((t) => t.always)
	if (always) return always
	const bySex = userSex ? targets.find((t) => t.sex === userSex) : null
	return bySex ?? targets[0]
}

// Module-scoped promise cache — the registry is fetched once per session and shared
// across every `/fwf/<slug>` navigation. A failed fetch is NOT cached so a later
// navigation retries.
let _registryCache: Promise<FwfEntry[]> | null = null

export function loadFwfRegistry(): Promise<FwfEntry[]> {
	if (!_registryCache) {
		// Bare axios, not the shared `@/helpers/request`: its default client swallows
		// errors (so this catch never sees them) and Sentry-logs every one. Own the
		// call so a genuine registry-unreachable failure is reported exactly once and
		// the funnel degrades gracefully (→ null entry → fallback redirect).
		_registryCache = axios
			.get(config('FwfResolverUrl'), { timeout: 15000 })
			.then((res): FwfEntry[] => (Array.isArray(res.data) ? res.data : []))
			.catch((e) => {
				Sentry.captureException(e)
				_registryCache = null
				return []
			})
	}
	return _registryCache
}

/**
 * The entry a slug names, or null when it is unregistered — or `preview` on a build
 * that must not serve previews.
 *
 * A preview entry does not exist as far as production is concerned. Returning null
 * (rather than a disabled entry) is deliberate: callers already treat a missing entry
 * as "redirect to the landing", so an unpromoted pre-funnel behaves exactly like one
 * that was never published.
 *
 * The gate is the build's own environment, read straight from the env helper. A config
 * option would have been a copy of it under a second name — and one that getConfig then
 * had to map BACK to the environment for staging builds (they run with MODE=production),
 * which is a rule with no information in it.
 */
export function selectFwfEntry(registry: FwfEntry[], slug: string): FwfEntry | null {
	const normalized = slug.startsWith('/') ? slug : `/${slug}`
	const entry = registry.find((e) => e.url === normalized) ?? null
	if (entry?.preview && !ENV_DEV_OR_STAGING) return null
	return entry
}

export async function resolveFwfEntry(slug: string): Promise<FwfEntry | null> {
	return selectFwfEntry(await loadFwfRegistry(), slug)
}
